import { useState } from 'react';
import { VStack, cssVar, Flex, HStack, Icon, Avatar, Text } from '@cad-arc';
import {
    EntityTypes,
    Entity,
    Person,
    Vehicle,
    MergedEntityTypes,
    Property,
} from '../../../../fr-entity-panels/types';
import {
    PERSON_ENTITY_TYPES,
    CARD_BUTTON_SECTION_WIDTH,
    PROPERTY_ENTITY_TYPES,
} from '../../constants';
import { PersonCardTileFields } from './components/person-card-tile-fields';
import { PersonCardExpandableContent } from './components/person-card-expandable-content';
import { VehicleCardTileFields } from './components/vehicle-card-tile-fields';
import { VehicleCardExpandableContent } from './components/vehicle-card-expandable-content';
import { CARD_CONTAINERS_PADDING } from './constants';
import { DexDetailsRedirectionButton } from './components/dex-details-redirection-button';
import { PropertyCardTileFields } from './components/property-card-tile-fields';
import { PropertyCardExpandableContent } from './components/property-card-expandable-content';
import { getEntityIconAvatar } from './helpers';
import { CommonSize, getBasicCommonSize } from '../../../../common/helpers/utils';
import { useIsFederatedSearchEnabledButNotAvailable } from '../../../../federated-search/federated-search-remote-state-context';

export function EntityCard({
    type,
    entity,
    index,
    showDexDetails = true,
    size = 'sm',
    showViewOnMap = true,
}: {
    type: EntityTypes | MergedEntityTypes;
    entity: Entity;
    index: number;
    showDexDetails?: boolean;
    size?: CommonSize;
    showViewOnMap?: boolean;
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const isFederatedSearchEnabledButNotAvailable = useIsFederatedSearchEnabledButNotAvailable();
    const isPersonCard = PERSON_ENTITY_TYPES.includes(type);
    const isPropertyCard = PROPERTY_ENTITY_TYPES.includes(type);
    const entityCardIconAvatar = getEntityIconAvatar(isPersonCard, isPropertyCard, type);
    const expandableContent =
        isPersonCard && !isPropertyCard ? (
            <PersonCardExpandableContent
                person={entity as Person}
                size={size}
                showViewOnMap={showViewOnMap}
            />
        ) : !isPersonCard && isPropertyCard ? (
            <PropertyCardExpandableContent property={entity as Property} entityType={type} />
        ) : (
            <VehicleCardExpandableContent
                vehicle={entity as Vehicle}
                entityType={type}
                size={size}
            />
        );

    return (
        <Flex
            flexDirection="column"
            width="100%"
            alignItems="flex-start"
            borderRadius={`${cssVar('arc.space.1')}`}
            borderWidth="1px"
            borderColor="border.default"
            backgroundColor="surface.foreground"
        >
            <HStack alignItems="flex-start" justifyContent="space-between" width="100%">
                <HStack
                    alignItems="flex-start"
                    margin={0}
                    padding={`${CARD_CONTAINERS_PADDING} 0 ${CARD_CONTAINERS_PADDING} ${CARD_CONTAINERS_PADDING}`}
                    width={`calc(100%-${CARD_BUTTON_SECTION_WIDTH})`}
                >
                    <Avatar size={size ?? 'md'} icon={entityCardIconAvatar} />
                    <VStack alignItems="flex-start" justifyContent="flex-start">
                        {isPersonCard && !isPropertyCard ? (
                            <PersonCardTileFields
                                person={entity as Person}
                                index={index}
                                entityType={type}
                                size={size}
                            />
                        ) : !isPersonCard && isPropertyCard ? (
                            <PropertyCardTileFields
                                property={entity as Property}
                                entityType={type}
                            />
                        ) : (
                            <VehicleCardTileFields
                                vehicle={entity as Vehicle}
                                index={index}
                                entityType={type}
                                size={size}
                            />
                        )}
                        <HStack
                            alignItems="center"
                            onClick={() => setIsExpanded((previous) => !previous)}
                            _hover={{ cursor: 'pointer' }}
                            role="button"
                        >
                            <Text fontSize={size} color="info" fontWeight="bold">
                                View Details
                            </Text>
                            <Icon
                                color="brand.default"
                                size={getBasicCommonSize(size)}
                                icon="Open"
                                style={{
                                    ...(isExpanded ? { transform: 'rotate(180deg)' } : {}),
                                    transition: '0s',
                                }}
                            />
                        </HStack>
                    </VStack>
                </HStack>
                {!isFederatedSearchEnabledButNotAvailable && showDexDetails && (
                    <DexDetailsRedirectionButton entity={entity} entityType={type} />
                )}
            </HStack>
            {isExpanded && expandableContent}
        </Flex>
    );
}
